import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import { palette } from "../../themes/theme";
import { useState } from "react";
import { CustomPolygonClass } from "./utilities/CustomPolygon";
import PrimaryTextField from "../PrimaryTextField";
import DarkPrimaryButton from "../PrimaryButton/DarkPrimaryButton";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { CustomDraggable } from "../Draggable";
import { hexToRgba } from "../../utils/helper";
import { useSelector } from "react-redux";
import ColorPicker from "../ColorPicker";
import {
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
} from "@mui/icons-material";
import { CustomTextBoxClass } from "./utilities/CustomTextbox";
import HaloEffectRange from "../HaloEffectRange";

const CustomMenuItem = styled(MenuItem)(({ theme }) => ({
  color: palette.text.dark,
}));

export const TextStyleSetting = ({
  mapRef,
  drawRef,
  setTextSelected,
  resetAllOnceChanged,
  textBoxSetttings,
  currentModeRef,
  setTextBox,
  setMarkers,
  markerRef,
}) => {
  const textBoxStyleSettings = useSelector(
    (state) => state.mapSettings.textBoxSetting
  );
  const fontStyles = [
    { value: "none", key: "Regular" },
    { value: "bold", key: "Bold" },
    { value: "italic", key: "Italic" },
    { value: "boldItalic", key: "Bold & Italic" },
  ];

  const fontFamilies = [
    { value: "Arial", key: "Arial" },
    { value: "Verdana", key: "Verdana" },
    { value: "Helvetica", key: "Helvetica" },
    { value: "Times New Roman", key: "Times New Roman" },
    { value: "Courier New", key: "Courier New" },
    { value: "monospace", key: "Monospace" },
    { value: "Roboto", key: "Roboto" },
  ];
  const [textBoxStyleLocally, setTextBoxStyleLocally] = useState({
    textBoxFontColor: textBoxSetttings.style.font.color ?? "",
    textBoxFontFamily: textBoxSetttings.style.font.fontFamily ?? "",
    textBoxFontSize: textBoxSetttings.style.font.size ?? 1,
    textBoxFontStyle: textBoxSetttings.style.font.style ?? "",
    textBoxFontAlignment: textBoxSetttings.style.font.alignment ?? "",
    textBoxFillColor: textBoxSetttings.style.textBox.fillColor ?? "",
    textBoxFillColorTransparent:
      textBoxSetttings.style.textBox.isFillTransparent ?? true,
    textBoxBorderColor: textBoxSetttings.style.textBox.borderColor ?? "",
    textBoxBorderWidth: textBoxSetttings.style.textBox.borderWidth ?? 1,
    textBoxBorderColorTransparent:
      textBoxSetttings.style.textBox.isBorderTransparent ?? true,
    textBoxOpacity: textBoxSetttings.style.textBox.opacity ?? 1,
    textBoxHaloEffect: textBoxSetttings.style.textBox.haloEffect ?? "",
    textBoxHaloEffect_x: textBoxSetttings.style.textBox.haloEffect_x ?? 0,
    textBoxHaloEffect_y: textBoxSetttings.style.textBox.haloEffect_y ?? 0,
    textBoxHaloEffect_b: textBoxSetttings.style.textBox.haloEffect_b ?? 0,
    textBoxHaloEffect_c: textBoxSetttings.style.textBox.haloEffect_c ?? 0,
    textBoxDropShadow: textBoxSetttings.style.textBox.dropShadow ?? "",
    textBoxDropShadow_x: textBoxSetttings.style.textBox.dropShadow_x ?? 0,
    textBoxDropShadow_y: textBoxSetttings.style.textBox.dropShadow_y ?? 0,
    textBoxDropShadow_b: textBoxSetttings.style.textBox.dropShadow_b ?? 0,
    textBoxDropShadow_c: textBoxSetttings.style.textBox.dropShadow_c ?? 0,
    isHaloEffectTransparent:
      textBoxSetttings.style.textBox.isHaloEffectTransparent ?? true,
    isDropShadowTransparent:
      textBoxSetttings.style.textBox.isDropShadowTransparent ?? true,
    leaderLineColor: textBoxSetttings.style.leaderLine.color ?? "",
    leaderLineWidth: textBoxSetttings.style.leaderLine.width ?? 1,
    isLineTransparent:
      textBoxSetttings.style.leaderLine.isLineTransparent ?? true,
    currentSetting: textBoxSetttings.style.textBox.currentSetting,
    width: textBoxSetttings.style.textBox.width ?? 0,
    height: textBoxSetttings.style.textBox.height ?? 0,
    content: textBoxSetttings.style.textBox.content ?? "",
    rotate: textBoxSetttings.style.textBox.rotate ?? 0,
    lat: textBoxSetttings.style.textBox.lat ?? "",
    lng: textBoxSetttings.style.textBox.lng ?? "",
    coordinates: textBoxSetttings.style.leaderLine.coordinates ?? "",
  });

  const changeStyle = (key, value) => {
    switch (key) {
      case "textBoxDropShadow-transparent":
        value = value.target.checked;
        key = "isDropShadowTransparent";
        break;
      case "textBoxFillColor-transparent":
        value = value.target.checked;
        key = "textBoxFillColorTransparent";
        break;
      case "textBoxBorderColor-transparent":
        value = value.target.checked;
        key = "textBoxBorderColorTransparent";
        break;
      case "leaderLineColor-transparent":
        value = value.target.checked;
        key = "isLineTransparent";
        break;
      case "transparency":
        value = value.target.value;
        key = "textBoxOpacity";
        break;
      default:
        break;
    }

    const tempStyle = {
      ...textBoxStyleLocally,
      [key]: value,
    };

    setTextBoxStyleLocally(tempStyle);
    const data = {
      font: {
        color: tempStyle.textBoxFontColor,
        fontFamily: tempStyle.textBoxFontFamily,
        size: tempStyle.textBoxFontSize,
        style: tempStyle.textBoxFontStyle,
        alignment: tempStyle.textBoxFontAlignment,
      },
      textBox: {
        fillColor: hexToRgba(
          tempStyle.textBoxFillColor,
          tempStyle.textBoxOpacity
        ),
        isFillTransparent: tempStyle.textBoxFillColorTransparent,
        borderColor: tempStyle.textBoxBorderColor,
        borderWidth: tempStyle.textBoxBorderWidth,
        isBorderTransparent: tempStyle.textBoxBorderColorTransparent,
        opacity: tempStyle.textBoxOpacity,
        haloEffect: tempStyle.textBoxHaloEffect,
        haloEffect_x: tempStyle.textBoxHaloEffect_x,
        haloEffect_y: tempStyle.textBoxHaloEffect_y,
        haloEffect_b: tempStyle.textBoxHaloEffect_b,
        haloEffect_c: tempStyle.textBoxHaloEffect_c,
        isHaloEffectTransparent: tempStyle.isHaloEffectTransparent,
        dropShadow: tempStyle.textBoxDropShadow,
        dropShadow_x: tempStyle.textBoxDropShadow_x,
        dropShadow_y: tempStyle.textBoxDropShadow_y,
        dropShadow_b: tempStyle.textBoxDropShadow_b,
        dropShadow_c: tempStyle.textBoxDropShadow_c,
        isDropShadowTransparent: tempStyle.isDropShadowTransparent,
        currentSetting: tempStyle.currentSetting,
        width: textBoxSetttings.style.textBox.width,
        height: textBoxSetttings.style.textBox.height,
        content: textBoxSetttings.style.textBox.content,
        rotate: tempStyle.rotate,
        lat: textBoxSetttings.style.textBox.lat,
        lng: textBoxSetttings.style.textBox.lng,
      },
      leaderLine: {
        color: tempStyle.leaderLineColor,
        width: tempStyle.leaderLineWidth,
        isLineTransparent: tempStyle.isLineTransparent,
        coordinates: textBoxSetttings.style.leaderLine.coordinates,
      },
    };
    CustomTextBoxClass.OnStyleChangeTextBox({
      inputMarkerId: textBoxSetttings.id,
      connectedLeaderLine: textBoxSetttings.leaderLine,
      changedStyle: data,
      setTextBox,
      mapDraw: drawRef,
      textBoxDefaultStyle: textBoxStyleSettings,
      currentMode: currentModeRef,
      map: mapRef,
      polygonDefaultStyle: CustomPolygonClass.polygonDefaultStyle,
      // setUndoHistory,
      // setRedoHistory,
      setDrawnFeaturesSupport: () => {},
      isUpdate: true,
      resetAllOnceChanged,
    });
  };

  const handleRemove = () => {
    CustomTextBoxClass.DeleteTextBox({
      inputMarkerId: textBoxSetttings.id || null,
      mapDraw: drawRef,
      setTextBox,
      // setUndoHistory,
      // setRedoHistory,
      setDrawnFeaturesSupport: () => {},
      isDelete: true,
      map: mapRef,
      markerRef,
    });
    setMarkers(markerRef.current);
    // drawRef.delete(featureRef.current.id);
    // setTextSelected(null);
    resetAllOnceChanged();
  };

  const handleOk = () => {
    // Apply the current local style changes to the text box
    if (textBoxSetttings.selectedTextBox) {
      // Format the data properly for OnStyleChangeTextBox
      const data = {
        font: {
          color: textBoxStyleLocally.textBoxFontColor,
          fontFamily: textBoxStyleLocally.textBoxFontFamily,
          size: textBoxStyleLocally.textBoxFontSize,
          style: textBoxStyleLocally.textBoxFontStyle,
          alignment: textBoxStyleLocally.textBoxFontAlignment,
        },
        textBox: {
          fillColor: hexToRgba(
            textBoxStyleLocally.textBoxFillColor,
            textBoxStyleLocally.textBoxOpacity
          ),
          isFillTransparent: textBoxStyleLocally.textBoxFillColorTransparent,
          borderColor: textBoxStyleLocally.textBoxBorderColor,
          borderWidth: textBoxStyleLocally.textBoxBorderWidth,
          isBorderTransparent: textBoxStyleLocally.textBoxBorderColorTransparent,
          opacity: textBoxStyleLocally.textBoxOpacity,
          haloEffect: textBoxStyleLocally.textBoxHaloEffect,
          haloEffect_x: textBoxStyleLocally.textBoxHaloEffect_x,
          haloEffect_y: textBoxStyleLocally.textBoxHaloEffect_y,
          haloEffect_b: textBoxStyleLocally.textBoxHaloEffect_b,
          haloEffect_c: textBoxStyleLocally.textBoxHaloEffect_c,
          isHaloEffectTransparent: textBoxStyleLocally.isHaloEffectTransparent,
          dropShadow: textBoxStyleLocally.textBoxDropShadow,
          dropShadow_x: textBoxStyleLocally.textBoxDropShadow_x,
          dropShadow_y: textBoxStyleLocally.textBoxDropShadow_y,
          dropShadow_b: textBoxStyleLocally.textBoxDropShadow_b,
          dropShadow_c: textBoxStyleLocally.textBoxDropShadow_c,
          isDropShadowTransparent: textBoxStyleLocally.isDropShadowTransparent,
          currentSetting: textBoxStyleLocally.currentSetting,
          width: textBoxSetttings.style.textBox.width,
          height: textBoxSetttings.style.textBox.height,
          content: textBoxSetttings.style.textBox.content,
          rotate: textBoxStyleLocally.rotate,
          lat: textBoxSetttings.style.textBox.lat,
          lng: textBoxSetttings.style.textBox.lng,
        },
        leaderLine: {
          color: textBoxStyleLocally.leaderLineColor,
          width: textBoxStyleLocally.leaderLineWidth,
          isLineTransparent: textBoxStyleLocally.isLineTransparent,
          coordinates: textBoxSetttings.style.leaderLine.coordinates,
        },
      };
      
      CustomTextBoxClass.OnStyleChangeTextBox({
        inputMarkerId: textBoxSetttings.selectedTextBox.id,
        connectedLeaderLine: textBoxSetttings.selectedTextBox.leaderLine,
        changedStyle: data,
        setTextBox,
        mapDraw: drawRef,
        textBoxDefaultStyle: textBoxStyleSettings,
        currentMode: currentModeRef,
        map: mapRef,
        polygonDefaultStyle: CustomPolygonClass.polygonDefaultStyle,
        setDrawnFeaturesSupport: () => {},
        isUpdate: true,
        resetAllOnceChanged,
        markers: markerRef.current,
      });
    }
    
    // Close the popup by clearing the selectedTextBox state
    setTextBox((previousValue) => {
      return { ...previousValue, selectedTextBox: null };
    });
  };
  return (
    <CustomDraggable
      width={300}
      height={500}
      customHeader={
        <>
          <div
            style={{
              display: "flex",
              gap: 12,
              padding: "8px 0px 8px 10px",
              cursor: "all-scroll",
            }}
            className="draggable-header"
          >
            <DragIndicatorIcon color="secondary" />
            <Typography sx={{ color: palette.text.dark, fontWeight: "bold" }}>
              Text Settings
            </Typography>
          </div>
          <Box
            sx={{
              // pl: 1,
              pt: 2,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", gap: 1 }}>
              <ColorPicker
                //   checked={polygonStyleRef.current.polygonColorTransparent}
                color={textBoxStyleLocally.textBoxFontColor}
                noCheckbox
                setColor={changeStyle}
                type={"textBoxFontColor"}
              />
              <FormControl fullWidth>
                <InputLabel sx={{ top: "-10px" }}>Font family</InputLabel>
                <Select
                  sx={{
                    color: palette.text.dark,
                    py: 0,
                    "& .MuiSelect-outlined": { py: "5px" },
                  }}
                  value={textBoxStyleLocally.textBoxFontFamily}
                  label="fontFamily"
                  onChange={(e) =>
                    changeStyle("textBoxFontFamily", e.target.value)
                  }
                >
                  {fontFamilies.map((d, i) => (
                    <CustomMenuItem key={i} value={d.value}>
                      {d.key}
                    </CustomMenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ position: "relative" }}>
              <PrimaryTextField
                type="number"
                showSpinBtn={false}
                hideBorderOnError={!textBoxStyleLocally.textBoxFontSize > 14}
                sx={{
                  border:
                    textBoxStyleLocally.textBoxFontSize > 14
                      ? "none"
                      : "2px solid red",
                  borderRadius: "4px",
                  width: "50px"
                }}
                defaultValue={textBoxStyleLocally.textBoxFontSize}
                onChange={(e) => changeStyle("textBoxFontSize", e.target.value)}
              />
              {textBoxStyleLocally.textBoxFontSize < 15 && (
                <span
                  style={{
                    position: "absolute",
                    color: "red",
                    fontSize: "x-small",
                    bottom: "-12px",
                    left: 0,
                  }}
                >
                  Value should be greater than 14
                </span>
              )}
              <FormControl sx={{ width: "110px", pl: "4px" }}>
                <InputLabel sx={{ top: "-10px" }}>Font style</InputLabel>
                <Select
                  sx={{
                    color: palette.text.dark,
                    py: 0,
                    "& .MuiSelect-outlined": { py: "5px" },
                  }}
                  value={textBoxStyleLocally.textBoxFontStyle}
                  label="fontStyle"
                  onChange={(e) =>
                    changeStyle("textBoxFontStyle", e.target.value)
                  }
                >
                  {fontStyles.map((d, i) => (
                    <CustomMenuItem key={i} value={d.value}>
                      {d.key}
                    </CustomMenuItem>
                  ))}
                </Select>
              </FormControl>
              <IconButton
                onClick={() => changeStyle("textBoxFontAlignment", "left")}
              >
                <FormatAlignLeft fontSize="small" />
              </IconButton>
              <IconButton
                onClick={() => changeStyle("textBoxFontAlignment", "center")}
              >
                <FormatAlignCenter fontSize="small" />
              </IconButton>
              <IconButton
                onClick={() => changeStyle("textBoxFontAlignment", "right")}
                sx={{ pr: 0 }}
              >
                <FormatAlignRight fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </>
      }
      customFooter={
        <>
          <Button variant="outlined" sx={{ fontWeight: "bold" }} onClick={handleOk}>Ok</Button>
          <Button variant="outlined" sx={{ fontWeight: "bold" }} onClick={handleRemove}>Remove</Button>
        </>
      }
    >
      <Box sx={{ p: "18px 24px 0px 18px" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Typography sx={{ color: palette.text.dark, fontSize: 15 }}>
            Rotation:
          </Typography>
          <Slider
            min={0}
            step={1}
            max={360}
            defaultValue={textBoxStyleLocally.rotate}
            valueLabelDisplay="auto"
            onChange={(e) => changeStyle("rotate", e.target.value)}
          />
          <Typography
            sx={{
              color: palette.text.dark,
              fontSize: 15,
            }}
          >
            Background Settings
          </Typography>
          <ColorPicker
            title={"Fill"}
            //   checked={polygonStyleRef.current.polygonColorTransparent}
            color={textBoxStyleLocally.textBoxFillColor}
            checked={textBoxStyleLocally.textBoxFillColorTransparent}
            setColor={changeStyle}
            type={"textBoxFillColor"}
          />
          <Typography sx={{ color: palette.text.dark, fontSize: 15 }}>
            Transparency:
          </Typography>
          <Slider
            min={0}
            step={1}
            max={100}
            defaultValue={textBoxStyleLocally.textBoxOpacity}
            valueLabelDisplay="auto"
            onChange={(e) => changeStyle("transparency", e)}
          />
          <Typography
            sx={{
              color: palette.text.dark,
              fontSize: 15,
            }}
          >
            Border Settings
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
            }}
          >
            <ColorPicker
              checked={textBoxStyleLocally.textBoxBorderColorTransparent}
              color={textBoxStyleLocally.textBoxBorderColor}
              setColor={changeStyle}
              type={"textBoxBorderColor"}
            />
            <PrimaryTextField
              type="number"
              showSpinBtn={false}
              sx={{ 
                width: "65px"
               }}
              label="Size"
              defaultValue={textBoxStyleLocally.textBoxBorderWidth}
              onChange={(e) =>
                changeStyle("textBoxBorderWidth", e.target.value)
              }
            />
          </Box>
          <Typography
            sx={{
              color: palette.text.dark,
              fontSize: 15,
            }}
          >
            Leader Line Settings
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
            }}
          >
            <ColorPicker
              // title={"Border"}
              // checked={polygonStyleRef.current.polygonBorderColorTransparent}
              // color={polygonStyleRef.current.polygonBorderColor}
              checked={textBoxStyleLocally.isLineTransparent}
              color={textBoxStyleLocally.leaderLineColor}
              setColor={changeStyle}
              type={"leaderLineColor"}
            />
            <PrimaryTextField
              type="number"
              showSpinBtn={false}
              sx={{ 
                width: "65px"
               }}
              label="Width"
              defaultValue={textBoxStyleLocally.leaderLineWidth}
              onChange={(e) => changeStyle("leaderLineWidth", e.target.value)}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    sx={{ p: 0 }}
                    defaultChecked={textBoxStyleLocally.isDropShadowTransparent}
                    onChange={(e) =>
                      changeStyle(`textBoxDropShadow-transparent`, e)
                    }
                  />
                }
                sx={{ m: 0 }}
              />
              <Typography
                sx={{
                  color: palette.text.dark,
                  fontSize: 15,
                }}
              >
                Drop Shadow
              </Typography>
            </Box>
            <ColorPicker
              width={20}
              height={20}
              color={textBoxStyleLocally.textBoxDropShadow}
              rightAlign={true}
              noCheckbox={true}
              setColor={changeStyle}
              type={"textBoxDropShadow"}
            />
          </Box>
          <HaloEffectRange
            title="B"
            value={textBoxStyleLocally.textBoxDropShadow_b}
            type="textBoxDropShadow_b"
            onChange={changeStyle}
            min={0}
            max={10}
            disabled={!textBoxStyleLocally.isDropShadowTransparent}
          />

          <HaloEffectRange
            title="X"
            type="textBoxDropShadow_x"
            value={textBoxStyleLocally.textBoxDropShadow_x}
            onChange={changeStyle}
            min={-20}
            max={20}
            disabled={!textBoxStyleLocally.isDropShadowTransparent}
          />

          <HaloEffectRange
            title="Y"
            type="textBoxDropShadow_y"
            value={textBoxStyleLocally.textBoxDropShadow_y}
            onChange={changeStyle}
            min={-20}
            max={20}
            disabled={!textBoxStyleLocally.isDropShadowTransparent}
          />
        </Box>
      </Box>
    </CustomDraggable>
  );
};
